/**
 * Firebase config for dynapp.
 *
 * - Auth: Google sign-in for intent dispatch (interacting)
 * - Local E2E: optional Auth emulator wiring via Vite env vars
 *
 * Firestore is no longer used — real-time session state comes via WebSocket
 * from the flux-service changestream endpoint (CON-3412).
 */
import { initializeApp } from 'firebase/app';
import { connectAuthEmulator, getAuth, GoogleAuthProvider } from 'firebase/auth';

function parseHostPort(hostOrUrl: string | undefined): { host: string; port: number } | null {
  const raw = (hostOrUrl ?? '').trim();
  if (!raw) return null;

  const normalized = raw.startsWith('http://') || raw.startsWith('https://')
    ? new URL(raw)
    : new URL(`http://${raw}`);

  const port = Number(normalized.port || '80');
  if (!normalized.hostname || Number.isNaN(port)) return null;
  return { host: normalized.hostname, port };
}

const isStaging =
  typeof window !== 'undefined' &&
  (window.location.hostname.includes('staging') ||
    window.location.hostname === 'localhost');

const STAGING_CONFIG = {
  apiKey: 'AIzaSyCyY0F_1HBpeNqq7VcZhhpJrxx6intckgA',
  authDomain: 'continua-staging.firebaseapp.com',
  projectId: 'continua-staging',
};

const PROD_CONFIG = {
  apiKey: 'AIzaSyDJ2BO1j0sULWWPBTX7HcjANQ-FXCqnCSU',
  authDomain: 'continua-client.firebaseapp.com',
  projectId: 'continua-client',
};

const app = initializeApp(isStaging ? STAGING_CONFIG : PROD_CONFIG);

export const auth = getAuth(app);
export const googleProvider = new GoogleAuthProvider();
export const FLUX_API_BASE = import.meta.env.VITE_FLUX_API_BASE || (isStaging
  ? 'https://flux-service-staging-329044329780.us-central1.run.app'
  : 'https://flux-service-prod-329044329780.us-central1.run.app');

if (import.meta.env.DEV) {
  const authEmulator = parseHostPort(import.meta.env.VITE_FIREBASE_AUTH_EMULATOR_HOST);
  if (authEmulator) {
    connectAuthEmulator(auth, `http://${authEmulator.host}:${authEmulator.port}`, {
      disableWarnings: true,
    });
  }
}

export async function devSignInWithEmailPassword(email: string, password: string): Promise<void> {
  if (!import.meta.env.DEV) return;
  const { signInWithEmailAndPassword } = await import('firebase/auth');
  await signInWithEmailAndPassword(auth, email, password);
}

export { isStaging };
